import React, { ReactElement, useState } from 'react';
import {
  Button, FormGroup, Input, Loader, LoaderButton, Textarea,
} from 'idsk-react-components';
import { ConfirmModal, TextEditor } from 'slovlex-idsk-react-components';
import { useNavigate, useParams } from 'react-router';
import FormElementWrapper from '../../../FormElements/FormElementWrapper';
import useFieldErrorMessages from '../../../useFieldErrorMessages';
import { ObalMaterialu } from '../../../../model/sprievodnyDokument/obalMaterialu/ObalMaterialuFormData';
import './vseobecnyObalMaterialu.scss';
import { useClient } from '../../../../context/ClientStatusContext';
import routes from '../../../../routing/routes';
import { SafeParameterSetter } from '../../../../types/SafeParameterSetter';
import { useSuccessMessageContext } from '../../../../context/SuccessMessageContext';
import { ClientResponse } from '../../../../model/ClientResponse';
import ObalVseobecnyFillSystemDataButton from './component/ObalVseobecnyFillSystemDataButton';
import {
  fetchObalMaterialuVseobecnyInitData,
} from '../../../../client/pracovnyKrok/sprievodnyDokument/obalMaterialuInitDataClient';
import { pouzivatelIsAdmin } from '../../../../function/permissions';
import { useUserContext } from '../../../../context/UserContext';
import { ClientStatus } from '../../../../model/ClientStatus';

interface ObalMaterialuProps {
  initFormData: ObalMaterialu;
  onSubmitForm: (formData: ObalMaterialu) => Promise<ClientResponse<void>>;
  submitStatus: ClientStatus;
}

export const VseobecnyObalMaterialu = ({
  initFormData,
  onSubmitForm,
  submitStatus,
}: ObalMaterialuProps): ReactElement => {
  const [obalMaterialu, setObalMaterialu] = useState<ObalMaterialu>(initFormData);
  const { clearFieldErrorMessage, getFieldErrorMessage } = useFieldErrorMessages();
  const { displaySuccess, clearMessages } = useSuccessMessageContext();

  const navigate = useNavigate();
  const {
    type,
    year,
    index,
    stadiumUuid,
    // @ts-ignore
  } = useParams<{ type: string, year: number, index: number, stadiumUuid: string }>();
  const { pouzivatel } = useUserContext();

  const updateField: SafeParameterSetter<ObalMaterialu> = (fieldName, value) => {
    setObalMaterialu(prevState => ({
      ...prevState,
      [fieldName]: value,
    }));
    clearFieldErrorMessage(fieldName);
  };

  const {
    wrapClient: fillSystemDataClient,
    status: fillSystemDataStatus,
  } = useClient(fetchObalMaterialuVseobecnyInitData);

  const onSubmit = (): void => {
    onSubmitForm(obalMaterialu).then(() => {
      navigate(
        pouzivatelIsAdmin(pouzivatel)
          ? routes.procesy.detail.sprievodneDokumenty.editAsAdmin.withParams(type, year, index, stadiumUuid)
          : routes.procesy.detail.sprievodneDokumenty.edit.withParams(type, year, index),
      );
      displaySuccess('Dokument bol úspešne uložený');
    }).catch(() => { /* noop */ });
  };

  return (
    <Loader loading={fillSystemDataStatus?.loading || false}>
      <>
        <ObalVseobecnyFillSystemDataButton
          setObalMaterialu={setObalMaterialu}
          fillSystemDataStatus={fillSystemDataStatus}
          fillSystemDataClient={fillSystemDataClient}
        />
        <FormElementWrapper
          width="one-half"
          required={false}
          label="Názov vygenerovaného dokumentu"
          fieldName="nazovVygenerovanehoDokumentu"
          getFieldErrorMessage={getFieldErrorMessage}
        >
          <span id="hint" className="idsk-hint">Zadajte názov, pod ktorým bude dokument pridaný medzi
            sprievodnú dokumentáciu.
          </span>
          <Input
            type="text"
            value={obalMaterialu?.nazovVygenerovanehoDokumentu}
            error={getFieldErrorMessage && getFieldErrorMessage('nazovVygenerovanehoDokumentu')}
            onChange={({ target }) => updateField('nazovVygenerovanehoDokumentu', target.value)}
            maxLength={64}
          />
        </FormElementWrapper>
        <FormElementWrapper
          width="one-half"
          required={false}
          label="Zodpovedná inštitúcia"
          fieldName="zodpovednaInstitucia"
          getFieldErrorMessage={getFieldErrorMessage}
        >
          <span id="hint" className="idsk-hint">Vysledný vygenerovaný dokument má limitovanú výšku.
            Ak bude text obsahovať viac ako tri riadky, nebude vo výslednom dokumente zobrazený.
          </span>
          <Textarea
            error={getFieldErrorMessage && getFieldErrorMessage('zodpovednaInstitucia')}
            id="zodpovednaInstitucia"
            type="text"
            name="zodpovednaInstitucia"
            value={obalMaterialu?.zodpovednaInstitucia ?? ''}
            onChange={({ target }) => updateField && updateField('zodpovednaInstitucia', target.value)}
            rows={4}
            maxLength={128}
            showRemaining
            expandHeight
          />
        </FormElementWrapper>
        <FormElementWrapper
          width="one-half"
          required={false}
          label="Číslo"
          fieldName="rezortneCislo"
          getFieldErrorMessage={getFieldErrorMessage}
        >
          <Input
            type="text"
            value={obalMaterialu?.rezortneCislo}
            error={getFieldErrorMessage && getFieldErrorMessage('rezortneCislo')}
            onChange={({ target }) => updateField && updateField('rezortneCislo', target.value)}
            maxLength={64}
          />
        </FormElementWrapper>
        <FormElementWrapper
          width="one-half"
          required={false}
          label="Typ obalu materiálu"
          fieldName="typObalu"
          getFieldErrorMessage={getFieldErrorMessage}
        >
          <Input
            type="text"
            value={obalMaterialu?.typObalu}
            error={getFieldErrorMessage && getFieldErrorMessage('typObalu')}
            onChange={({ target }) => updateField && updateField('typObalu', target.value)}
            maxLength={64}
          />
        </FormElementWrapper>
        <div className="centered-items__container">
          <FormElementWrapper
            sectionClass="flex-items__section"
            width="full"
            required={false}
            label="Nadpis riadok 1 - Návrh"
            fieldName="navrh"
            getFieldErrorMessage={getFieldErrorMessage}
          >
            <Input
              type="text"
              value={obalMaterialu?.navrh}
              error={getFieldErrorMessage && getFieldErrorMessage('navrh')}
              onChange={({ target }) => updateField && updateField('navrh', target.value)}
              maxLength={64}
            />
          </FormElementWrapper>
          <FormElementWrapper
            sectionClass="flex-items__section"
            width="full"
            required={false}
            label="Nadpis riadok 2 - Typ materiálu"
            fieldName="typMaterialu"
            getFieldErrorMessage={getFieldErrorMessage}
          >
            <Input
              type="text"
              value={obalMaterialu?.typMaterialu}
              error={getFieldErrorMessage && getFieldErrorMessage('typMaterialu')}
              onChange={({ target }) => updateField && updateField('typMaterialu', target.value)}
              maxLength={64}
            />
          </FormElementWrapper>
          <FormElementWrapper
            sectionClass="flex-items__section"
            width="full"
            required={false}
            label="Nadpis riadok 3 - Rok"
            fieldName="rok"
            getFieldErrorMessage={getFieldErrorMessage}
          >
            <Input
              type="text"
              value={obalMaterialu?.rok}
              error={getFieldErrorMessage && getFieldErrorMessage('rok')}
              onChange={({ target }) => updateField && updateField('rok', target.value)}
              maxLength={64}
            />
          </FormElementWrapper>
        </div>
        <FormElementWrapper
          sectionClass="nazov-materialu__container"
          width="full"
          required={false}
          label="Nadpis riadok 4 - Názov materiálu"
          fieldName="nazovMaterialu"
          getFieldErrorMessage={getFieldErrorMessage}
        >
          <span id="hint" className="idsk-hint">Vysledný vygenerovaný dokument má limitovanú výšku.
            Ak bude text obsahovať veľa riadkov, nemusí sa do dokumentu zmestiť celý.
          </span>
          <Textarea
            error={getFieldErrorMessage && getFieldErrorMessage('nazovMaterialu')}
            id="nazovMaterialu"
            type="text"
            name="nazovMaterialu"
            value={obalMaterialu?.nazovMaterialu ?? ''}
            onChange={({ target }) => updateField && updateField('nazovMaterialu', target.value)}
            rows={4}
            maxLength={330}
            showRemaining
            expandHeight
          />
        </FormElementWrapper>
        <div className="row-items">
          <FormElementWrapper
            sectionClass="flex-items__section"
            width="full"
            required={false}
            label="Podnet"
            fieldName="podnet"
            getFieldErrorMessage={getFieldErrorMessage}
          >
            <span id="hint" className="idsk-hint">Vysledný vygenerovaný dokument má limitovanú výšku.
              Ak bude text obsahovať veľa riadkov, nemusí sa do dokumentu zmestiť celý.
            </span>
            <Textarea
              error={getFieldErrorMessage && getFieldErrorMessage('podnet')}
              id="podnet"
              type="text"
              name="podnet"
              value={obalMaterialu?.podnet ?? ''}
              onChange={({ target }) => updateField && updateField('podnet', target.value)}
              rows={4}
              maxLength={660}
              showRemaining
              expandHeight
            />
          </FormElementWrapper>
          <FormElementWrapper
            sectionClass="flex-items__section"
            width="full"
            required={false}
            label="Obsah materiálu"
            fieldName="obsahMaterialu"
            getFieldErrorMessage={getFieldErrorMessage}
          >
            <span id="hint" className="idsk-hint">Prosím, vložte maximálne 13 typov dokumentov.
              Ak bude text obsahovať veľa riadkov, nemusí sa do dokumentu zmestiť celý.
            </span>
            <Textarea
              error={getFieldErrorMessage && getFieldErrorMessage('obsahMaterialu')}
              id="obsahMaterialu"
              type="text"
              name="obsahMaterialu"
              value={obalMaterialu?.obsahMaterialu ?? ''}
              onChange={({ target }) => updateField && updateField('obsahMaterialu', target.value)}
              rows={4}
              maxLength={512}
              showRemaining
              expandHeight
            />
          </FormElementWrapper>
        </div>
        <FormElementWrapper
          required={false}
          label="Predkladá"
          fieldName="predkladatel"
          getFieldErrorMessage={getFieldErrorMessage}
        >
          <span id="hint" className="idsk-hint">Výsledný vygenerovaný dokument má limitovanú výšku.
            Ak bude text obsahovať viac ako štyri riadky, nebude vo výslednom dokumente zobrazený.
          </span>
          <TextEditor
            initialState={initFormData.predkladatel}
            onChange={data => updateField && updateField('predkladatel', data)}
            maxLength={128}
            showRemaining
          />
        </FormElementWrapper>
        <div className="centered-items__container">
          <FormElementWrapper
            sectionClass="flex-items__section"
            width="full"
            required={false}
            label="Miesto a dátum"
            fieldName="miestoDatum"
            getFieldErrorMessage={getFieldErrorMessage}
          >
            <Input
              type="text"
              value={obalMaterialu?.miestoDatum}
              error={getFieldErrorMessage && getFieldErrorMessage('miestoDatum')}
              onChange={({ target }) => updateField('miestoDatum', target.value)}
              maxLength={64}
            />
          </FormElementWrapper>
        </div>
        <FormGroup className="obal-materialu-vseobecny__form-buttons">
          <ConfirmModal
            question="Naozaj si prajete uložiť formulár a vytvoriť sprievodný dokument?"
            description={'Ak upravujete existujúci formulár, budú údaje tohto formulára prepísané '
              + 'a výsledný dokument, ktorý vznikol na základe tohto formulára, bude nahradený.'}
            cancelButtonText="Nie"
            confirmButton={
                (
                  <Button
                    buttonStyle="action"
                    size="large"
                  >
                    Áno
                  </Button>
                )
              }
            openButton={(
              <LoaderButton
                loading={submitStatus?.loading || false}
                type="submit"
                size="large"
                disableOutsideClickWhileLoading
              >Uložiť
              </LoaderButton>
              )}
            doAfterConfirm={() => {
              clearMessages();
              onSubmit();
            }}
          />
          <Button
            className="obal_materialu__buttons"
            type="button"
            buttonStyle="third"
            size="large"
            disabled={submitStatus?.loading || false}
            onClick={() => navigate(-1)}
          >Späť
          </Button>
        </FormGroup>
      </>
    </Loader>
  );
};

export default VseobecnyObalMaterialu;
